import { TFunction } from 'i18next';
import queryString from 'query-string';

import {
  IDocumentTypeDependentField,
  IDocumentTypeField,
  IDocumentTypeFields,
  ITableField
} from '@store/modules/metadata/types'
import { fieldValue, Nullable } from '@store/types/commonTypes'
import { DocumentFieldDependency, IDocumentFieldsDependencies, IDocumentValuesFields, IFieldGenerationSettings } from '@store/modules/documents/types'
import { DependencyType, FieldType, FIXED_POSITION_FIELDS, Instance, MathOperationType, Space } from '@const/consts'
import { ISourceFieldsCalculationParams } from '@utils/types'
import { displayBasicNotification, getDetailedTableFilterParams, getFieldsDivSub, INameInterface } from './utils'
import { Document } from '@app/const/translations'
import { ITableHandbooks } from '@app/views/organization/documents/components/Document/components/TableFields/types'
import { getSurnameWithInitials, ILibDetailedTableCellFilterOptions } from '@infologistics/frontend-libraries'
import { getClientScriptDependencies } from './clientScript'
import { IUserFilter } from '@app/store/modules/userFilters/types'
import { IHandbook } from '@store/modules/handbooks/types'
import i18n from '@app/i18n';
import store from '@app/store/configureStore';

export const getHandbookFieldRelationKey = (
  fieldKey: string,
  fields?: Nullable<IDocumentTypeFields>
): string => {
  const dictType = fields?.[fieldKey]?.dictType

  if (!dictType) return fieldKey

  const fieldsWithSameDictType = Object.values(fields ?? {})
    .filter((field) => field.dictType === dictType)

  return fieldsWithSameDictType.length > 1
    ? `${dictType}::${fieldKey}`
    : dictType
}

export const getDocumentCountHint = (
  t: TFunction,
  packageDocumentCount = 0,
  relatedDocumentCount = 0
): string =>
  t('documents:countFilesHint', {
    packageDocumentCount,
    relatedDocumentCount
  })

export const filterFieldsForGeneration = <T extends IDocumentValuesFields>(
  fields: T,
  documentTypeFields: IDocumentTypeFields,
  settings?: IFieldGenerationSettings
): T => Object.keys(fields).reduce((acc, key) => {
  const isConfigurable = documentTypeFields[key]?.formProperties?.form.isConfigurableOnCreation

  if (isConfigurable && settings?.[key] === false) return acc

  return {
    ...acc,
    [key]: fields[key]
  }
}, {} as T)

export const getClearedDocumentFieldValue = ({ isArray, type }: IDocumentTypeField): fieldValue => {
  if (isArray && (type === FieldType.DICTIONARY || type === FieldType.FILE)) return []
  if (type === FieldType.BOOLEAN) return false

  return null
}

export const getDependentFieldsResetValues = (
  sourceFieldKey: string,
  fields: IDocumentTypeFields,
  dependentFields: Nullable<IDocumentTypeDependentField[]>
): Record<string, fieldValue> => {
  const resetValues: Record<string, fieldValue> = {}
  const checkedFieldKeys = new Set<string>()
  const validDependentFields = filterFieldsDependencies(fields, dependentFields) ?? []

  const collectResetValues = (currentSourceFieldKey: string): void => {
    if (checkedFieldKeys.has(currentSourceFieldKey)) return

    checkedFieldKeys.add(currentSourceFieldKey)

    validDependentFields
      .filter(({ sourceFieldKeys }) => sourceFieldKeys.includes(currentSourceFieldKey))
      .forEach(({ fieldKey }) => {
        const dependentField = fields[fieldKey]

        if (!dependentField) return

        resetValues[fieldKey] = getClearedDocumentFieldValue(dependentField)
        collectResetValues(fieldKey)
      })
  }

  collectResetValues(sourceFieldKey)

  return resetValues
}

interface IGetRecalculatedDependentMathValuesParams {
  sourceFieldKeys: string[]
  fields: IDocumentTypeFields
  dependentFields: Nullable<IDocumentTypeDependentField[]>
  fieldValues: IDocumentValuesFields
  fieldGenerationSettings?: IFieldGenerationSettings
  formatMathValue: (value: number, roundTo: number) => fieldValue
}

export const getRecalculatedDependentMathValues = ({
  sourceFieldKeys,
  fields,
  dependentFields,
  fieldValues,
  fieldGenerationSettings,
  formatMathValue
}: IGetRecalculatedDependentMathValuesParams): Record<string, fieldValue> => {
  const mathDependencies = (filterFieldsDependencies(fields, dependentFields) ?? [])
    .filter(({ dependencyType }) => dependencyType === DependencyType.MATH_OPERATION)
  const dependenciesBySourceFieldKey = mathDependencies.reduce<Record<string, IDocumentTypeDependentField[]>>(
    (dependencies, dependency) => dependency.sourceFieldKeys.reduce((nextDependencies, dependencySourceFieldKey) => ({
      ...nextDependencies,
      [dependencySourceFieldKey]: [
        ...nextDependencies[dependencySourceFieldKey] ?? [],
        dependency
      ]
    }), dependencies),
    {}
  )
  const nextFieldValues = { ...fieldValues }
  const recalculatedValues: Record<string, fieldValue> = {}

  const hasFieldValue = (value: fieldValue): boolean => {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0

    if (typeof value === 'object' && 'value' in value) {
      return value.value !== null && value.value !== undefined && value.value.toString().length > 0
    }

    return value.toString().length > 0
  }

  const recalculateDependents = (sourceFieldKey: string, calculationPath = new Set<string>()): void => {
    if (calculationPath.has(sourceFieldKey)) return

    const nextCalculationPath = new Set([...calculationPath, sourceFieldKey])

    dependenciesBySourceFieldKey[sourceFieldKey]?.forEach((dependency) => {
      const dependentField = fields[dependency.fieldKey]

      if (!dependentField || nextCalculationPath.has(dependency.fieldKey)) return

      const isDisabledDependentField =
        dependentField.formProperties?.form.isConfigurableOnCreation === true &&
        fieldGenerationSettings?.[dependency.fieldKey] === false

      if (isDisabledDependentField) return

      const hasDisabledOrEmptySource = dependency.sourceFieldKeys.some((dependencySourceFieldKey) => (
        (
          fields[dependencySourceFieldKey]?.formProperties?.form.isConfigurableOnCreation === true &&
          fieldGenerationSettings?.[dependencySourceFieldKey] === false
        ) || !hasFieldValue(nextFieldValues[dependencySourceFieldKey])
      ))
      let nextValue = getClearedDocumentFieldValue(dependentField)

      if (!hasDisabledOrEmptySource) {
        const calculation = getSourceFieldsCalculation({
          sourceFieldKeys: dependency.sourceFieldKeys,
          mathOperationType: dependency.mathOperationType ?? '',
          fields: nextFieldValues
        })

        if (isNaN(calculation)) return

        nextValue = formatMathValue(calculation, dependency.mathOperationRoundTo ?? 0)
      }

      nextFieldValues[dependency.fieldKey] = nextValue
      recalculatedValues[dependency.fieldKey] = nextValue
      recalculateDependents(dependency.fieldKey, nextCalculationPath)
    })
  }

  sourceFieldKeys.forEach((sourceFieldKey) => recalculateDependents(sourceFieldKey))

  return recalculatedValues
}

interface IGetRestoredDependentFieldsValuesParams {
  sourceFieldKey: string
  fields: IDocumentTypeFields
  dependentFields: Nullable<IDocumentTypeDependentField[]>
  fieldValues: IDocumentValuesFields
  fieldGenerationSettings?: IFieldGenerationSettings
  isMathCalculationEnabled: boolean
  formatMathValue: (value: number, roundTo: number) => fieldValue
  loadDictionaryRelations: (
    sourceDictType: string,
    sourceKeys: string[],
    dependentFieldKey: string,
    dependentDictType: string
  ) => Promise<IHandbook[]>
}

export const getRestoredDependentFieldsValues = async ({
  sourceFieldKey,
  fields,
  dependentFields,
  fieldValues,
  fieldGenerationSettings,
  isMathCalculationEnabled,
  formatMathValue,
  loadDictionaryRelations
}: IGetRestoredDependentFieldsValuesParams): Promise<Record<string, fieldValue>> => {
  const validDependentFields = filterFieldsDependencies(fields, dependentFields) ?? []
  const dependencyByFieldKey = new Map(validDependentFields.map((dependency) => [dependency.fieldKey, dependency]))
  const affectedFieldKeys = new Set([
    sourceFieldKey,
    ...Object.keys(getDependentFieldsResetValues(sourceFieldKey, fields, validDependentFields))
  ])
  const nextFieldValues = { ...fieldValues }
  const restoredValues: Record<string, fieldValue> = {}
  const restoredFieldKeys = new Set<string>()
  const restoringFieldKeys = new Set<string>()

  const getDictionaryKeys = (value: fieldValue): string[] => {
    const dictionaryValues = Array.isArray(value) ? value : [value]

    return dictionaryValues.reduce<string[]>((keys, dictionaryValue) => {
      if (typeof dictionaryValue === 'string') return dictionaryValue ? [...keys, dictionaryValue] : keys

      if (
        typeof dictionaryValue === 'object' &&
        dictionaryValue !== null &&
        'key' in dictionaryValue &&
        dictionaryValue.key
      ) {
        return [...keys, String(dictionaryValue.key)]
      }

      return keys
    }, [])
  }

  const hasFieldValue = (value: fieldValue): boolean => {
    if (value === null || value === undefined || value === '') return false
    if (Array.isArray(value)) return value.length > 0

    if (typeof value === 'object' && 'value' in value) {
      return value.value !== null && value.value !== undefined && value.value.toString().length > 0
    }

    return value.toString().length > 0
  }

  const restoreField = async (fieldKey: string): Promise<void> => {
    if (restoredFieldKeys.has(fieldKey) || restoringFieldKeys.has(fieldKey)) return

    const dependency = dependencyByFieldKey.get(fieldKey)

    if (!dependency) {
      restoredFieldKeys.add(fieldKey)
      return
    }

    restoringFieldKeys.add(fieldKey)

    for (const dependencySourceFieldKey of dependency.sourceFieldKeys) {
      if (affectedFieldKeys.has(dependencySourceFieldKey)) {
        await restoreField(dependencySourceFieldKey)
      }
    }

    const dependentField = fields[fieldKey]
    const isDisabledConfigurableField =
      dependentField?.formProperties?.form.isConfigurableOnCreation === true &&
      fieldGenerationSettings?.[fieldKey] === false
    const hasDisabledSource = dependency.sourceFieldKeys.some((dependencySourceFieldKey) => (
      fields[dependencySourceFieldKey]?.formProperties?.form.isConfigurableOnCreation === true &&
      fieldGenerationSettings?.[dependencySourceFieldKey] === false
    ))

    if (!dependentField || isDisabledConfigurableField || hasDisabledSource) {
      restoringFieldKeys.delete(fieldKey)
      restoredFieldKeys.add(fieldKey)
      return
    }

    if (dependency.dependencyType === DependencyType.DICT_LOOKUP) {
      const dependencySourceFieldKey = dependency.sourceFieldKeys[0]
      const sourceDictType = fields[dependencySourceFieldKey]?.dictType
      const dependentDictType = dependentField.dictType

      if (sourceDictType && dependentDictType) {
        const relationValues = await loadDictionaryRelations(
          sourceDictType,
          getDictionaryKeys(nextFieldValues[dependencySourceFieldKey]),
          fieldKey,
          dependentDictType
        )
        const singleRelationValue = relationValues.length === 1 ? relationValues[0] : undefined
        const nextValue = dependentField.isArray
          ? relationValues.map(({ key, value }) => ({ key, value }))
          : singleRelationValue
            ? { key: singleRelationValue.key, value: singleRelationValue.value }
            : getClearedDocumentFieldValue(dependentField)

        nextFieldValues[fieldKey] = nextValue
        restoredValues[fieldKey] = nextValue
      }
    }

    if (
      dependency.dependencyType === DependencyType.MATH_OPERATION &&
      isMathCalculationEnabled &&
      dependency.sourceFieldKeys.every((dependencySourceFieldKey) => hasFieldValue(nextFieldValues[dependencySourceFieldKey]))
    ) {
      const calculation = getSourceFieldsCalculation({
        sourceFieldKeys: dependency.sourceFieldKeys,
        mathOperationType: dependency.mathOperationType ?? '',
        fields: nextFieldValues
      })

      if (!isNaN(calculation)) {
        const nextValue = formatMathValue(calculation, dependency.mathOperationRoundTo ?? 0)
        nextFieldValues[fieldKey] = nextValue
        restoredValues[fieldKey] = nextValue
      }
    }

    restoringFieldKeys.delete(fieldKey)
    restoredFieldKeys.add(fieldKey)
  }

  for (const affectedFieldKey of affectedFieldKeys) {
    await restoreField(affectedFieldKey)
  }

  return restoredValues
}

export const getFieldsDependencies = (
  fields: IDocumentTypeFields,
  dependentFields: Nullable<IDocumentTypeDependentField[]>,
  tableFields?: ITableField[]
): IDocumentFieldsDependencies => {
  let calculable = {}
  return filterFieldsDependencies(fields, dependentFields, tableFields)?.reduce((acc, field) => {
    // dependent fields
    if (field.dependencyType === DependencyType.DICT_LOOKUP) {
      // if influencing field is a handbook - the key is its dictType
      // otherwise the key is the name of the influencing field
     const tableFieldWithKey = tableFields?.find(tableField => tableField.key === field.fieldKey)
      const key: string = fields[field?.sourceFieldKeys[0]]?.dictType ??  tableFields?.find(tableField => tableField.key === field?.sourceFieldKeys[0])?.dictType ?? field?.sourceFieldKeys[0]
      if (key in (acc?.dependent ?? {})) {
        return { ...acc, dependent: { ...acc.dependent, [key]: [...acc.dependent?.[key] ?? [], fields[field?.fieldKey]?.dictType ?? tableFieldWithKey?.dictType] } }
      }

      return { ...acc, dependent: { ...acc.dependent, [key]: [fields[field?.fieldKey]?.dictType ?? tableFieldWithKey?.dictType] } }
    }

    // calculable fields
    if (field.dependencyType === DependencyType.MATH_OPERATION) {
      const keys: string[] = field.sourceFieldKeys ?? []

      keys?.forEach(key => {
        if (key in calculable) {
          calculable = { ...calculable, [key]: [...calculable?.[key] ?? [], field.fieldKey] }
          return
        }
        calculable = { ...calculable, [key]: [field.fieldKey] }
      })
    }

    return { ...acc, calculable }
  }, { dependent: {}, calculable: {} }) ?? {}
}

export const filterFieldsDependencies = (
  fields: IDocumentTypeFields,
  dependentFields: Nullable<IDocumentTypeDependentField[]>,
  tableFields?: ITableField[]
): Nullable<IDocumentTypeDependentField[]> => {
  if (!dependentFields) return null

  return dependentFields.filter(dependentField => !getIsFieldBroken(Object.keys(fields), dependentField, tableFields))
}

export const getIsFieldBroken = (
  fieldsKeys: string[],
  dependentField: IDocumentTypeDependentField,
  tableFields?: ITableField[]
) => {
  const { fieldKey, sourceFieldKeys } = dependentField

  const tableFieldsKeys = tableFields?.map(field => field.key) ?? []

  // The field removed from type
  const isDeleted = ![...fieldsKeys, ...tableFieldsKeys].includes(fieldKey)

  // The field is in both the list of main fields and the list of table fields
  const isMainField = fieldsKeys.includes(fieldKey)
  const isInTwoStructure = !!(isMainField && tableFields?.find(tableField => tableField.key === fieldKey))
  const areBothTypes = isMainField && sourceFieldKeys.some(key => !fieldsKeys.includes(key))

  // The field is the same field type as its influencing/dependent field
  let isSameType = false
  let isBothSourceDict = false
  if (tableFields?.find(field => field.key === fieldKey)) {
    sourceFieldKeys.forEach(sourceFieldKey => {
      const foundField = tableFields.find(field => field.key === sourceFieldKey)
      const foundMainField = fieldsKeys.includes(sourceFieldKey)
      if (!foundField && (tableFields.find(field => field.key === fieldKey)?.type !== FieldType.DICTIONARY)) isSameType = true
      if (foundField && foundMainField && (tableFields.find(field => field.key === sourceFieldKey)?.type === FieldType.DICTIONARY)) isBothSourceDict = true
    })
  }

  return isDeleted || isInTwoStructure || areBothTypes || isSameType || isBothSourceDict
}

const getSourceFieldValue = (key: string, fields: IDocumentValuesFields, handbooks: ITableHandbooks | undefined, tableFields: ITableField[] | undefined) => {
  const dictType = tableFields?.find((field) => field.key === key)?.dictType

  if (dictType) {
    const dictValue = handbooks?.[dictType ?? ''].find(pair => pair.key === fields[key])?.value
    return Number(dictValue?.toString().replace(',', '.'))
  }

  const fieldValue = fields[key]

  return fieldValue && typeof fieldValue === 'object' && 'value' in fieldValue
    ? Number(fieldValue.value.toString().replace(',', '.'))
    : Number(fieldValue?.toString().replace(',', '.'))
}

export const getSourceFieldsCalculation = ({
  sourceFieldKeys,
  mathOperationType,
  fields,
  handbooks,
  tableFields
}: ISourceFieldsCalculationParams): number => {
  let result = 0

  const sourceFieldValues : number[] = sourceFieldKeys.map((key) => {
    return getSourceFieldValue(key, fields, handbooks, tableFields)
  })

  switch (mathOperationType) {
    case MathOperationType.MULTIPLY:
      result = sourceFieldValues.reduce((acc, val) => acc * val);
      break
    case MathOperationType.ADD:
      result = sourceFieldValues.reduce((acc, val) => acc + val);
      break
    case MathOperationType.DIVIDE: {
      const firstFieldArray = getFieldsDivSub(sourceFieldKeys);

      result = firstFieldArray.restFields.reduce(
        (acc, val) =>
          acc / getSourceFieldValue(val, fields, handbooks, tableFields),
        getSourceFieldValue(
          firstFieldArray.firstField ?? '',
          fields,
          handbooks,
          tableFields
        )
      );
      break;
    }
    case MathOperationType.SUBSTRACTION: {
      const firstFieldArray = getFieldsDivSub(sourceFieldKeys);

      result = firstFieldArray.restFields.reduce(
        (acc, val) =>
          acc - getSourceFieldValue(val, fields, handbooks, tableFields),
        getSourceFieldValue(
          firstFieldArray.firstField ?? '',
          fields,
          handbooks,
          tableFields
        )
      );
      break;
    }
  }

  return result
}

/** Получение массива полей без фиксированной позиции с сортировкой по index  */
export const getUnfixedDocFields = (fields: IDocumentTypeFields) => {
  const fieldsArr: IDocumentTypeField[] = [];

  Object.entries(fields).forEach(([key, value]) => {
    if (!FIXED_POSITION_FIELDS.includes(key)) {
      fieldsArr.push(value);
    }
  });

  fieldsArr.sort((val1, val2) => val1.index - val2.index);

  return fieldsArr;
};

export const getIsBotName = (user: INameInterface) => {
  const { name, surname, patronymic } = user;
  return (
    (surname === 'System' && name === 'bot' && !patronymic) ||
    (surname === 'ILS' && name === 'bot' && !patronymic)
  );
};

/** Функция вывода ФИО с учётом вывода полного имени для бота / вывода полного имени для инстанса VN */
export const getSurnameWithInitialsBot = (user: INameInterface) => {
  const { name, surname, patronymic, language } = user;
  const { instance } = store.getState().utils;

  return getIsBotName(user)
    ? surname + Space.STANDART + name
    : instance === Instance.VIETNAMESE
      ? `${surname ? surname + ' ' : ''}${name ? name + ' ' : ''}${patronymic ? patronymic + ' ' : ''}`.trim()
      : getSurnameWithInitials({ name, patronymic, surname, language });
};

/** Функция поиска циклической зависимости */
export const findCircularDependency = (
  dependentFields: Nullable<IDocumentTypeDependentField[]>,
  clientScript: Nullable<string>
) => {
  const dependentObj: DocumentFieldDependency = {};

  if (dependentFields) {
    dependentFields.forEach((val) => {
      dependentObj[val.fieldKey] = val.sourceFieldKeys;
    });
  }

  if (clientScript) {
    const clientScriptDependencies = getClientScriptDependencies(clientScript)
    for (const key of Object.keys(clientScriptDependencies)) {
      dependentObj[key] = [
        ...dependentObj[key] ? dependentObj[key] : [],
        ...clientScriptDependencies[key]
      ]
    }
  }

  let fieldsErrors = {};

  const searchCircularDependency = (
    entry: string,
    path = new Set<string>([])
  ) => {
    const branch = dependentObj[entry];
    const newPath = new Set([...path, entry]);

    if (branch) {
      branch.forEach((newEntry) => {
        if (path.has(newEntry)) {
          path.forEach((val) => {
            fieldsErrors = {
              ...fieldsErrors,
              [val]: Document.dependencyRuleError,
            };
          });
          fieldsErrors = {
            ...fieldsErrors,
            [entry]: Document.dependencyRuleError,
          };
        } else {
          searchCircularDependency(newEntry, newPath);
        }
      });
    }
    // else {
    //   console.log("All OK");
    // }
  };

  for (const key of Object.keys(dependentObj)) {
    searchCircularDependency(key);
  }

  let finalFieldsErrors = {
    ...fieldsErrors,
  }
  for (const key of Object.keys(fieldsErrors)) {
    if (key in dependentObj) {
      dependentObj[key].forEach((val) => [
        finalFieldsErrors = {
          ...finalFieldsErrors,
          [val]: Document.dependencyRuleError,
        }
      ])
    }
  }

  return finalFieldsErrors;
};

/** Применение фильтра для детализированной таблицы */
export const setDetailedTableFilter = (
  search: string,
  fixedOption: ILibDetailedTableCellFilterOptions,
  callback: () => void,
  userFilter?: Nullable<IUserFilter>,
  userFilterUnsavedParams?: Nullable<string>
) => {
  const newParams = queryString.stringify(getDetailedTableFilterParams(fixedOption, ''));
  // console.log ("[setDetailedTableFilter]",{newParams, userFilterUnsavedParams, userFilter})
  if (userFilterUnsavedParams && !userFilterUnsavedParams.includes(newParams)) {
    // console.log ("[setDetailedTableFilter]-1",{newParams, userFilterUnsavedParams, userFilter})
    callback();
  } else if (!userFilterUnsavedParams && !userFilter?.params.includes(newParams) && !search.includes(newParams)) {
    // console.log ("[setDetailedTableFilter]-2",{newParams, userFilterUnsavedParams, userFilter})
    callback();
  } else {
    // console.log ("[setDetailedTableFilter]-3",{newParams, userFilterUnsavedParams, userFilter})
    displayBasicNotification({
      title: '',
      content: i18n.t('notification:notices.filterExist'),
      type: 'info'
    })
  }
};
